/**
 * Which page a feedback cold link lands on.
 *
 * A pasted link is the primary entry point for this feature, so every branch
 * below is reachable on a recipient's first click — before they have seen any
 * other console surface. Keeping the decision here rather than in the JSX is
 * what makes "signed out" and "signed in but not allowed to open this" two
 * different pages instead of one shrug; the browser client separates them for
 * exactly that reason.
 *
 * `getFeedbackAsksForRecipient` is the protocol's own assignment model, the
 * same one the respond card and the server read. A local "is this person a
 * recipient" filter would be a second opinion, and a second opinion is a place
 * to disagree with the server about whose question is whose.
 */

import {
  getFeedbackAsksForRecipient,
  type FeedbackRequestLifecycleStatus,
} from '@nimbalyst/collab-protocol';

import type { BrowserFeedbackRequestState } from './BrowserFeedbackRequestClient';

export type FeedbackRequestSurface =
  /** No projection yet, and nothing has failed. */
  | { kind: 'loading' }
  /** The session expired or never existed; the sign-in round trip fixes it. */
  | { kind: 'not-signed-in' }
  /** Signed in, and this org or membership will not open the room. */
  | { kind: 'not-authorized' }
  /** The room refused or the socket died for a reason the reader cannot fix. */
  | { kind: 'unavailable'; message: string }
  /** In the org, but nothing on this request is theirs to answer. */
  | { kind: 'not-a-recipient' }
  /** Open, with asks assigned to this viewer. */
  | { kind: 'respond' }
  /** Terminal lifecycle: the questions and the outcome are still readable. */
  | { kind: 'read-only'; status: Exclude<FeedbackRequestLifecycleStatus, 'open'> };

export function resolveFeedbackRequestSurface(
  state: BrowserFeedbackRequestState,
): FeedbackRequestSurface {
  // Terminal in the client, and both are about the reader rather than the
  // request, so they answer before anything the projection might hold.
  if (state.accessFailure === 'not-signed-in') return { kind: 'not-signed-in' };
  if (state.accessFailure === 'not-authorized') return { kind: 'not-authorized' };

  const request = state.request;
  if (!request) {
    if (state.status === 'error') {
      return {
        kind: 'unavailable',
        message: state.error?.message ?? 'This feedback request could not be opened.',
      };
    }
    return { kind: 'loading' };
  }

  // Whether the request is still open does not matter to someone it was never
  // addressed to: there is nothing for them to read late and nothing to do.
  if (!state.teamMemberId) return { kind: 'not-a-recipient' };
  if (getFeedbackAsksForRecipient(request, state.teamMemberId).length === 0) {
    return { kind: 'not-a-recipient' };
  }

  const status = request.lifecycle.status;
  return status === 'open' ? { kind: 'respond' } : { kind: 'read-only', status };
}
